import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase/config";
import { mapStorageError } from "../utils/storageErrors";

// Servizio F24 caricati direttamente dall'utente (sezione "Commercialista" della
// webapp). Storage: users/{uid}/f24/... . Firestore: users/{uid}/f24Uploads
// (coperta dalla regola wildcard esistente su users/{userId}/{subcollection=**}
// in firestore.rules). NB: distinta dalla collezione "f24" usata dalla pagina
// F24Page.jsx (gestione scadenze/pagamenti) e da "accountantUploads" usata dal
// portale commercialista token-based (functions/accountant.js).

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function f24UploadsCollection(uid) {
  return collection(db, "users", uid, "f24Uploads");
}

/**
 * Sottoscrive gli F24 caricati dall'utente, ordinati per anno decrescente.
 * @returns {Function} unsubscribe
 */
export function subscribeF24Uploads(uid, callback) {
  if (!uid) return () => {};

  const q = query(f24UploadsCollection(uid), orderBy("year", "desc"));
  const unsubscribe = onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(items);
    },
    (err) => {
      console.error("Errore lettura F24 caricati:", err);
      callback([]);
    }
  );

  return unsubscribe;
}

/**
 * Carica un F24 PDF su Storage e crea il relativo documento Firestore.
 */
export async function uploadF24(uid, { year, dueDate, amount, type, file }) {
  if (!uid) throw new Error("Utente non autenticato.");
  if (!year) throw new Error("Seleziona l'anno di riferimento.");
  if (!dueDate) throw new Error("Inserisci la data di scadenza.");
  if (type !== "acconto" && type !== "saldo") {
    throw new Error("Seleziona il tipo di F24 (acconto o saldo).");
  }
  if (!file) throw new Error("Seleziona un file da caricare.");
  if (file.type !== "application/pdf") {
    throw new Error("Formato non supportato. Carica un file PDF.");
  }
  if (file.size > MAX_FILE_SIZE) {
    throw new Error("File troppo grande. Massimo 10MB.");
  }

  const storagePath = `users/${uid}/f24/${year}_${Date.now()}.pdf`;
  const storageRef = ref(storage, storagePath);
  let fileUrl;
  try {
    await uploadBytes(storageRef, file);
    fileUrl = await getDownloadURL(storageRef);
  } catch (err) {
    throw mapStorageError(err);
  }

  await addDoc(f24UploadsCollection(uid), {
    year: Number(year),
    dueDate,
    amount: Number(amount) || 0,
    type,
    fileName: file.name,
    fileUrl,
    storagePath,
    uploadedAt: serverTimestamp(),
    uploadedBy: "user",
  });
}

/**
 * Elimina il documento Firestore e il relativo file su Storage (se presente).
 */
export async function deleteF24(uid, id, storagePath) {
  if (!uid || !id) throw new Error("Parametri mancanti per l'eliminazione.");

  await deleteDoc(doc(db, "users", uid, "f24Uploads", id));

  if (storagePath) {
    try {
      await deleteObject(ref(storage, storagePath));
    } catch (err) {
      // Ignora se il file non esiste più su Storage
      if (err.code !== "storage/object-not-found") {
        console.error("Errore eliminazione file F24 da Storage:", err);
      }
    }
  }
}
